import Image from 'next/image';
import {useState} from 'react';
import {MdTrendingUp} from 'react-icons/md';
import type {RakingListItemType} from '../../../../../core/entities/company/schema';
import {CommonLink} from '../../../../../shared/components/CommonLink';
import {useMediaQuery} from '../../../../../shared/services/useMediaQuery';
import {FollowCompanyButton} from '../FollowCompanyButton';

type Props = {
  rankingItem: RakingListItemType;
  isLarge?: boolean;
  isAllTerm?: boolean;
};

export const CompanyCardFollowRanking: React.FC<Props> = ({
  rankingItem,
  isLarge = false,
  isAllTerm = false,
}) => {
  const [showImage, setShowImage] = useState(
    Boolean(
      rankingItem.company.mainPhotoUrl &&
        rankingItem.company.pageType !== 'study',
    ),
  );
  const [showLogo, setShowLogo] = useState(
    Boolean(
      rankingItem.company.logoUrl && rankingItem.company.pageType !== 'study',
    ),
  );
  const companyPagePath = `/company/${rankingItem.company.id}`;

  const {isPC} = useMediaQuery();
  // 画像のサイズ調整
  const defaultWidth = 282;
  const imgWidth = (() => {
    if (typeof window === 'undefined' || isPC === undefined || isPC) {
      return isLarge ? defaultWidth : 200;
    }

    return isLarge ? (window?.screen.width ?? defaultWidth) - 32 : 300;
  })();

  const imgHeight = isPC ? (isLarge ? 141 : 100) : isLarge ? 149 : 125;

  return (
    <div
      className="mx-auto pc:mx-0 pc:hover:opacity-80"
      style={{width: `${imgWidth}px`}}
    >
      <CommonLink href={companyPagePath}>
        <div
          className="bg-black-4 overflow-hidden rounded-16 shadow-card relative"
          style={{
            height: `${imgHeight}px`,
            width: `${imgWidth}px`,
          }}
        >
          {showImage ? (
            <Image
              className="rounded-16 text-nowrap line-clamp-1 object-cover"
              src={rankingItem.company.mainPhotoUrl}
              width={imgWidth}
              height={imgHeight}
              alt={`${rankingItem.company.name}のメイン写真`}
              onError={() => {
                setShowImage(false);
              }}
              style={{
                height: '100%',
                width: '100%',
                objectFit: 'cover',
              }}
            />
          ) : (
            <div
              className="w-full"
              style={{
                height: `${imgHeight}px`,
              }}
            />
          )}
        </div>
      </CommonLink>

      <div className="flex justify-between">
        <CommonLink href={companyPagePath}>
          <div className="mt-[-16px] relative z-10 ml-8">
            {showLogo && (
              <div className="bg-white p-4 rounded-4 h-[38px] w-48 shadow-card flex justify-center items-center">
                <Image
                  src={rankingItem.company.logoUrl}
                  className="overflow-hidden line-clamp-1"
                  width={40}
                  height={30}
                  alt={`${rankingItem.company.name}のロゴ画像`}
                  onError={() => {
                    setShowLogo(false);
                  }}
                />
              </div>
            )}
          </div>
        </CommonLink>
        <div className={`flex mt-8 ${isLarge ? 'mr-12' : 'mr-8'}`}>
          <FollowCompanyButton
            companyId={rankingItem.company.id}
            isFollowed={rankingItem.isFollowed}
            followCount={rankingItem.count}
          />
        </div>
      </div>

      <div className="flex items-center gap-8">
        <div className="text-[30px] text-black font-bold">
          {rankingItem.rank}
        </div>
        <CommonLink href={companyPagePath}>
          <div className="line-clamp-2 font-bold text-md break-words">
            {rankingItem.company.name}
          </div>
        </CommonLink>
      </div>
      <div className="flex justify-between items-center mt-4">
        <div>
          {rankingItem.isHiring && (
            <div className="text-white text-sm rounded-16 bg-primary px-8 py-4">
              募集中
            </div>
          )}
        </div>
        <div className="flex items-center content-end gap-8">
          {!isAllTerm && (
            <div className="flex items-end text-black font-bold gap-4">
              <MdTrendingUp size={20} />
              {rankingItem.diff}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
